import React from "react";

import cx from "classnames";

import { ProfilePic } from "../ProfilePic";

import styles from "./Checkbox.scss";

export interface CheckboxProps {
  /**
   * Checked value for checkbox input
   */
  checked?: boolean;
  /**
   * Whether the checkbox is in an error state
   */
  hasError?: boolean;
  /**
   * Additional CSS class names to apply to the checkbox
   */
  className?: string;
  /**
   * Disables the checkbox from being changed
   */
  disabled?: boolean;
  /**
   * Extra text below checkbox label
   */
  helperText?: React.ReactNode;
  /**
   * Checkbox ID attribute (also links input to label)
   */
  id: string | number;
  /**
   * Checkbox Label Text
   */
  label?: React.ReactNode;
  /**
   * Checkbox name attribute
   */
  name?: string;
  /**
   * Value of the checkbox input (for form submission)
   */
  value?: string;
  /**
   * Function called when checkbox is clicked
   */
  onChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * Accessible label for the checkbox
   */
  ariaLabel?: string;
  /**
   * Determines font size of the label
   */
  size?: "extra-small" | "small" | "regular";
  /**
   * Whether the checkbox is in the indeterminate state
   */
  indeterminate?: boolean;
  /**
   * Function called when checkbox is clicked while indeterminate is true
   */
  onIndeterminateChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  /**
   * User object for the checkbox, if applicable
   */
  user?: {
    avatar?: string;
    id?: string;
    initials?: string;
    jobTitle?: string;
    name?: string | React.ReactNode;
  };
}

export function Checkbox({
  checked,
  className,
  disabled,
  helperText,
  id,
  label,
  name,
  onChange,
  onIndeterminateChange,
  ariaLabel,
  size = "regular",
  indeterminate = false,
  user,
  hasError,
  ...checkboxProps
}: CheckboxProps): JSX.Element {
  const classNames = cx(
    styles.wrapper,
    {
      [styles.wrapperDisabledState]: disabled,
    },
    {
      [styles["wrapper--xsmall"]]: size === "extra-small",
      [styles["wrapper--small"]]: size === "small",
    },
    className
  );

  const inputClassNames = cx(styles.input, {
    [styles["checkbox-indeterminate"]]: indeterminate,
  });

  const spanClassNames = cx(styles["checkbox"], {
    [styles["error"]]: hasError,
  });

  const handleChange = indeterminate
    ? onIndeterminateChange || onChange
    : onChange;

  return (
    <div data-testid={user ? "user-checkbox" : "checkbox"}>
      <label className={classNames} htmlFor={String(id)}>
        <input
          type="checkbox"
          aria-label={ariaLabel}
          className={inputClassNames}
          checked={checked}
          disabled={disabled}
          id={String(id)}
          name={name}
          data-indeterminate={indeterminate}
          onChange={handleChange}
          {...checkboxProps}
        />
        <span className={spanClassNames} />
        {user && <ProfilePic user={user} />}
        <div className={styles["label-container"]}>
          <span className={styles.label}>{label}</span>
          {(helperText || user?.jobTitle) && (
            <div className={styles["helper-row"]}>
              {user?.jobTitle && (
                <div className={styles["helper-text"]}>{user.jobTitle}</div>
              )}
              {helperText && (
                <div className={styles["helper-text"]}>{helperText}</div>
              )}
            </div>
          )}
        </div>
      </label>
    </div>
  );
}
